import * as React from 'react';
import { Input as TextInput } from '../../../components/ui';
import { Switch } from '../../../components/ui';
import InputMask from 'react-input-mask';
import { Formik, Form, FormikHelpers } from 'formik';
import Cards from 'react-credit-cards-2';
import * as yup from 'yup';
import { CreateSubscription } from '../../../services/account/AccountCheckout.service';
import { validations } from '../../messages';
import { SelectContainer } from './AccountCheckout.style';
import { getCardFlag } from '../../components';
import { CheckoutFormValues, Plan } from './AccountCheckout.type';
import { mapFormValuesToCheckoutRequest, generateInstallmentsArray } from './AccountCheckout.map';
import styled from 'styled-components';

const FormSection = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    color: #667eea;
    font-size: 28px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 18px 30px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(118, 75, 162, 0.4);
  margin-top: 20px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(118, 75, 162, 0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  i {
    margin-right: 8px;
    vertical-align: middle;
  }
`;

const ValidationMessage = styled.div<{ show: boolean; type: 'success' | 'error' }>`
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  margin-top: 5px;
  background: ${props => props.type === 'success' ? '#e8f5e9' : '#ffebee'};
  color: ${props => props.type === 'success' ? '#2e7d32' : '#c62828'};

  i {
    font-size: 18px;
  }
`;

interface AccountCheckoutFormProps {
  setPurchased: (value: boolean) => void;
  selectedPlan: Plan | null;
  setLoading: (value: boolean) => void;
  formToken: string;
  formRef?: React.RefObject<HTMLFormElement>;
}

export const AccountCheckoutFormEnhanced: React.FC<AccountCheckoutFormProps> = ({
  setPurchased,
  selectedPlan,
  setLoading,
  formToken,
  formRef,
}) => {
  const [focused, setFocused] = React.useState<string | null>(null);
  const [fieldValidation, setFieldValidation] = React.useState<Record<string, boolean>>({});
  
  if (!selectedPlan) return null;

  const installmentsArray = generateInstallmentsArray(selectedPlan.installments || 1);

  const checkoutSchema = yup.object().shape({
    showPayment: selectedPlan.store !== 'stripe',
    email: yup
      .string()
      .email('Digite um e-mail válido')
      .required('E-mail é obrigatório'),
    firstName: yup
      .string()
      .min(2, 'Nome muito curto')
      .required('Nome é obrigatório'),
    lastName: yup
      .string()
      .min(2, 'Sobrenome muito curto')
      .required('Sobrenome é obrigatório'),
    cpf: yup
      .string()
      .required('CPF é obrigatório')
      .test('cpf', 'Digite um CPF válido', (value) => {
        let strCPF = (value || '').replace(/\D/g, '');
        let Soma;
        let Resto;
        Soma = 0;
        if (strCPF == '00000000000') return false;

        for (let i = 1; i <= 9; i++) Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
        Resto = (Soma * 10) % 11;

        if (Resto == 10 || Resto == 11) Resto = 0;
        if (Resto != parseInt(strCPF.substring(9, 10))) return false;

        Soma = 0;
        for (let i = 1; i <= 10; i++) Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (12 - i);
        Resto = (Soma * 10) % 11;

        if (Resto == 10 || Resto == 11) Resto = 0;
        if (Resto != parseInt(strCPF.substring(10, 11))) return false;
        return true;
      }),
    phone: yup
      .string()
      .required('Telefone é obrigatório')
      .test('phone', 'Digite um telefone válido', (value) => {
        let telefone = value || '';
        telefone = telefone.replace(/\D/g, '');

        if (!(telefone.length >= 10 && telefone.length <= 11)) return false;

        if (telefone.length == 11 && parseInt(telefone.substring(2, 3)) != 9) return false;

        for (var n = 0; n < 10; n++) {
          if (telefone == new Array(11).join(String(n)) || telefone == new Array(12).join(String(n))) return false;
        }
        var codigosDDD = [11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 64, 63, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99];
        if (codigosDDD.indexOf(parseInt(telefone.substring(0, 2))) == -1) return false;
        if (telefone.length == 10 && [2, 3, 4, 5, 7].indexOf(parseInt(telefone.substring(2, 3))) == -1) return false;

        return true;
      }),
    creditCardNumber: yup
      .string()
      .when('showPayment', {
        is: true,
        then: yup.string().required('Número do cartão é obrigatório').min(19, 'Número do cartão inválido'),
      }),
    creditCardName: yup
      .string()
      .when('showPayment', {
        is: true,
        then: yup.string().required('Nome no cartão é obrigatório').min(3, 'Nome muito curto'),
      }),
    creditCardExpired: yup
      .string()
      .when('showPayment', {
        is: true,
        then: yup.string().required('Validade é obrigatória').min(5, 'Data inválida'),
      }),
    creditCardCvv: yup
      .string()
      .when('showPayment', {
        is: true,
        then: yup.string().required('CVV é obrigatório').min(3, 'CVV inválido'),
      }),
  });

  const validateField = async (fieldName: string, value: any, schema: any) => {
    try {
      await schema.validateAt(fieldName, { [fieldName]: value, showPayment: selectedPlan.store !== 'stripe' });
      setFieldValidation(prev => ({ ...prev, [fieldName]: true }));
      return true;
    } catch (error) {
      setFieldValidation(prev => ({ ...prev, [fieldName]: false }));
      return false;
    }
  };

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        cpf: '',
        gender: 'Masculino',
        phone: '',
        creditCardNumber: '',
        creditCardName: '',
        creditCardExpired: '',
        creditCardCvv: '',
        installments: 1,
      }}
      validationSchema={checkoutSchema}
      onSubmit={async (values: CheckoutFormValues, { setSubmitting }: FormikHelpers<CheckoutFormValues>) => {
        const body = mapFormValuesToCheckoutRequest(values, selectedPlan, formToken);
        
        setSubmitting(true);
        CreateSubscription(body, setLoading)
          .then((response) => {
            if (selectedPlan.store != 'stripe') {
              setPurchased(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          })
          .catch((error) => {
            console.error('Checkout error:', error);
          })
          .finally(() => {
            setSubmitting(false);
          });
      }}
    >
      {({ errors, touched, values, setValues, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldTouched }) => (
        <Form id="checkout-form" className="checkout-form" ref={formRef}>
          <FormSection>
            <SectionTitle>
              <i className="material-icons">person</i>
              Seus Dados Pessoais
            </SectionTitle>

            <div className="row">
              <div className="col s12">
                <TextInput
                  type="email"
                  name="email"
                  onChange={(e) => {
                    handleChange(e);
                    validateField('email', e.target.value, checkoutSchema);
                  }}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldTouched('email', true);
                  }}
                  value={values.email}
                  inputClassName={!!errors.email && !!touched.email ? 'invalid' : ' '}
                  error={touched.email ? errors.email : undefined}
                  required
                  label="E-mail*"
                />
                <ValidationMessage show={touched.email && !errors.email && values.email.length > 0} type="success">
                  <i className="material-icons">check_circle</i>
                  <span>E-mail válido</span>
                </ValidationMessage>
              </div>

              <div className="col s12" style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '10px' }}>
                  Gênero
                </label>
                <Switch
                  id="gender-check"
                  name="gender"
                  offLabel="Masculino"
                  onChange={(event) =>
                    event.target.checked
                      ? setValues({ ...values, gender: 'Feminino' })
                      : setValues({ ...values, gender: 'Masculino' })
                  }
                  onLabel="Feminino"
                />
              </div>

              <div className="col s12 m6">
                <TextInput
                  name="firstName"
                  onChange={(e) => {
                    handleChange(e);
                    validateField('firstName', e.target.value, checkoutSchema);
                  }}
                  onBlur={handleBlur}
                  value={values.firstName}
                  inputClassName={!!errors.firstName && !!touched.firstName ? 'invalid' : ' '}
                  error={touched.firstName ? errors.firstName : undefined}
                  required
                  label="Primeiro Nome*"
                />
              </div>

              <div className="col s12 m6">
                <TextInput
                  name="lastName"
                  onChange={(e) => {
                    handleChange(e);
                    validateField('lastName', e.target.value, checkoutSchema);
                  }}
                  onBlur={handleBlur}
                  value={values.lastName}
                  inputClassName={!!errors.lastName && !!touched.lastName ? 'invalid' : ' '}
                  error={touched.lastName ? errors.lastName : undefined}
                  required
                  label="Sobrenome*"
                />
              </div>

              <div className="col s12 m6 input-field">
                <InputMask
                  name="cpf"
                  type="tel"
                  mask={'999.999.999-99'}
                  onChange={(e) => {
                    handleChange(e);
                    validateField('cpf', e.target.value, checkoutSchema);
                  }}
                  onBlur={handleBlur}
                  value={values.cpf}
                  className={!!errors.cpf && !!touched.cpf ? 'invalid' : ' '}
                  required
                />
                <label htmlFor="cpf">CPF*</label>
                <span className="helper-text" data-error={touched.cpf ? errors.cpf : ''}></span>
                <ValidationMessage show={touched.cpf && !errors.cpf && values.cpf.length > 0} type="success">
                  <i className="material-icons">check_circle</i>
                  <span>CPF válido</span>
                </ValidationMessage>
              </div>

              <div className="col s12 m6 input-field">
                <InputMask
                  name="phone"
                  type="tel"
                  mask={'(99) 99999-9999'}
                  onChange={(e) => {
                    handleChange(e);
                    validateField('phone', e.target.value, checkoutSchema);
                  }}
                  onBlur={handleBlur}
                  value={values.phone}
                  className={!!errors.phone && !!touched.phone ? 'invalid' : ' '}
                  required
                />
                <label htmlFor="phone">Celular*</label>
                <span className="helper-text" data-error={touched.phone ? errors.phone : ''}></span>
                <ValidationMessage show={touched.phone && !errors.phone && values.phone.length > 0} type="success">
                  <i className="material-icons">check_circle</i>
                  <span>Telefone válido</span>
                </ValidationMessage>
              </div>
            </div>
          </FormSection>

          {selectedPlan.store !== 'stripe' && (
            <FormSection>
              <SectionTitle>
                <i className="material-icons">credit_card</i>
                Informações de Pagamento
              </SectionTitle>

              <div className="row">
                <div className="col s12" style={{ marginBottom: '20px' }}>
                  <Cards
                    number={values.creditCardNumber}
                    name={values.creditCardName}
                    expiry={values.creditCardExpired}
                    cvc={values.creditCardCvv}
                    focused={focused as any}
                    placeholders={{
                      name: 'NOME NO CARTÃO*',
                    }}
                    locale={{ valid: 'validade' }}
                  />
                </div>

                <div className="col s12 input-field">
                  <InputMask
                    id="creditCardNumber"
                    name="creditCardNumber"
                    type="tel"
                    mask={'9999 9999 9999 9999'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.creditCardNumber}
                    onFocus={() => setFocused('number')}
                    className={!!errors.creditCardNumber && !!touched.creditCardNumber ? 'invalid' : ' '}
                    required
                  />
                  <label htmlFor="creditCardNumber">Número do Cartão*</label>
                  {!!errors.creditCardNumber && touched.creditCardNumber && (
                    <span className="helper-text" data-error={errors.creditCardNumber}></span>
                  )}
                </div>

                <div className="col s12 input-field">
                  <TextInput
                    name="creditCardName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.creditCardName}
                    inputClassName={
                      !!errors.creditCardName && !!touched.creditCardName ? 'invalid' : ' '
                    }
                    error={touched.creditCardName ? errors.creditCardName : undefined}
                    required
                    label="Nome no Cartão*"
                    onFocus={() => setFocused('name')}
                  />
                </div>

                <div className="col s6 input-field">
                  <InputMask
                    name="creditCardExpired"
                    type="tel"
                    mask={'99/99'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={() => setFocused('expiry')}
                    value={values.creditCardExpired}
                    className={!!errors.creditCardExpired && !!touched.creditCardExpired ? 'invalid' : ' '}
                    required
                  />
                  <label htmlFor="creditCardExpired">Validade do Cartão*</label>
                  {!!errors.creditCardExpired && touched.creditCardExpired && (
                    <span className="helper-text" data-error={errors.creditCardExpired}></span>
                  )}
                </div>

                <div className="col s6 input-field">
                  <InputMask
                    name="creditCardCvv"
                    type="tel"
                    mask={'999'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.creditCardCvv}
                    onFocus={() => setFocused('cvc')}
                    className={!!errors.creditCardCvv && !!touched.creditCardCvv ? 'invalid' : ' '}
                    required
                  />
                  <label htmlFor="creditCardCvv">CVV*</label>
                  {!!errors.creditCardCvv && touched.creditCardCvv && (
                    <span className="helper-text" data-error={errors.creditCardCvv}></span>
                  )}
                </div>

                {!!selectedPlan && !!selectedPlan.installments && (
                  <div className="col s12">
                    <SelectContainer>
                      <select
                        name="installments"
                        className="browser-default"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.installments}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '16px',
                        }}
                      >
                        {installmentsArray.map((parcela) => (
                          <option key={parcela} value={parcela}>
                            {parcela}x de R$ {(selectedPlan.value / parcela).toFixed(2).replace('.', ',')}
                          </option>
                        ))}
                      </select>
                    </SelectContainer>
                  </div>
                )}
              </div>
            </FormSection>
          )}

          <SubmitButton type="submit" disabled={isSubmitting}>
            <i className="material-icons">lock</i>
            {isSubmitting ? 'Processando Pagamento...' : 'Finalizar Compra Segura'}
          </SubmitButton>

          <div style={{ textAlign: 'center', marginTop: '15px', color: '#999', fontSize: '14px' }}>
            <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle' }}>
              lock
            </i>{' '}
            Seus dados estão protegidos com criptografia SSL
          </div>
        </Form>
      )}
    </Formik>
  );
};

